import {
  MessagesSquare,
  FileText,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CalendarSync,
  ChartColumnIncreasing,
  ClipboardList,
  FileBarChart,
  HandCoins,
  Inbox,
  LayoutDashboard,
  Mail,
  Receipt,
  Repeat,
  Settings,
  ShieldCheck,
  Tags,
  Timer,
  UserPlus,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';

/*
 * The admin sidebar, as data.
 *
 * The groups, their labels and this exact order come from the prototype —
 * design-system.md §6. They are preserved deliberately: the people who will use
 * this have been using that layout, and reorganising it is a change to their
 * day, not an improvement to the code.
 *
 * Separated from the component so `isNavItemActive` below can be tested without
 * rendering anything. That function is defect P4 and it is worth a test.
 */

export interface NavItem {
  readonly label: string;
  readonly to: string;
  readonly icon: LucideIcon;
}

export interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

export const ADMIN_NAV: readonly NavGroup[] = [
  {
    label: 'Overview',
    items: [{ label: 'Dashboard', to: '/admin', icon: LayoutDashboard }],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Jobs', to: '/admin/jobs', icon: ClipboardList },
      { label: 'Calendar', to: '/admin/calendar', icon: CalendarDays },
      { label: 'Recurring Jobs', to: '/admin/recurring-jobs', icon: Repeat },
      { label: 'Job Requests', to: '/admin/job-requests', icon: Inbox },
      // Phase 9A. Customer correspondence — every message in and out, in one
      // place, so nobody opens Outlook to talk to a customer.
      { label: 'Conversations', to: '/admin/conversations', icon: MessagesSquare },
      // The one that breaks prefix matching. See isNavItemActive.
      { label: 'Calendar Sync', to: '/admin/calendar-sync', icon: CalendarSync },
      { label: 'Admins', to: '/admin/admins', icon: ShieldCheck },
    ],
  },
  {
    label: 'People',
    items: [
      { label: 'Leads', to: '/admin/leads', icon: UserPlus },
      { label: 'Applicants', to: '/admin/applicants', icon: Users },
      { label: 'Employees', to: '/admin/employees', icon: UsersRound },
      { label: 'Scheduling', to: '/admin/scheduling', icon: CalendarDays },
      { label: 'Timesheets', to: '/admin/timesheets', icon: ClipboardList },
      { label: 'Clients', to: '/admin/clients', icon: Building2 },
    ],
  },
  {
    label: 'Finance',
    items: [
      // First in the group, and in Finance rather than Settings, because
      // pricing is the reason anyone opens it — ROADMAP.md §3.3. Invoices sit
      // beside it, exactly as this comment promised phase 12 they would.
      { label: 'Services', to: '/admin/services', icon: HandCoins },
      { label: 'Invoices', to: '/admin/invoices', icon: FileText },
      { label: 'Expenses', to: '/admin/expenses', icon: Receipt },
      { label: 'Reports', to: '/admin/reports', icon: FileBarChart },
      // Owner ask, 2026-08-10. Beside Reports because it answers the same
      // questions with the same money — a picture rather than a spreadsheet.
      { label: 'Analytics', to: '/admin/analytics', icon: ChartColumnIncreasing },
    ],
  },
  {
    label: 'Settings',
    items: [
      { label: 'Property Categories', to: '/admin/property-categories', icon: Tags },
      // The job titles the careers form offers. Configuration of what the
      // business advertises, which is what this group is.
      { label: 'Positions', to: '/admin/positions', icon: BriefcaseBusiness },
      { label: 'Notification Emails', to: '/admin/notification-emails', icon: Mail },
      /*
       * **Not from the prototype** — the prototype has no background worker, so
       * it has nothing to show. ROADMAP.md §7.6 asks for the dashboard by name,
       * and a worker nobody can see is a worker nobody knows has stopped. In
       * Settings rather than Operations because it is about the machinery, not
       * about the day's work.
       */
      { label: 'Background Tasks', to: '/admin/worker', icon: Timer },
      { label: 'Settings', to: '/admin/settings', icon: Settings },
    ],
  },
];

/**
 * Whether a nav item should be highlighted for the current path.
 *
 * **This is defect P4, and it is the reason this is a function rather than a
 * `startsWith` inside the component.** The prototype matches nav items by
 * prefix, so at `/admin/calendar-sync` both "Calendar" *and* "Calendar Sync"
 * light up — the sidebar tells you you are in two places at once.
 *
 * Segment-wise matching fixes it: `/admin/calendar` matches `/admin/calendar`
 * and `/admin/calendar/2026-08`, but not `/admin/calendar-sync`, because
 * `calendar-sync` is a different segment from `calendar` rather than a longer
 * version of it.
 *
 * `/admin` itself is exact-only. It is the ancestor of every other item, so a
 * segment match would leave Dashboard lit on every page in the portal.
 */
export function isNavItemActive(itemPath: string, currentPath: string): boolean {
  const item = normalise(itemPath);
  const current = normalise(currentPath);

  if (item === current) return true;

  // The dashboard is a prefix of everything under it and is never a parent.
  if (item === '/admin') return false;

  // A trailing slash is what makes this segment-wise rather than character-wise.
  return current.startsWith(`${item}/`);
}

/** Drop a trailing slash so `/admin/jobs/` and `/admin/jobs` are the same place. */
function normalise(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Breadcrumbs for a path, built from the nav.
 *
 * Every admin page hangs off Dashboard, so that is always the first crumb. The
 * label comes from the nav rather than from the URL — `/admin/job-requests`
 * reads as "Job Requests", never as `job-requests`. Never render a raw slug
 * (CLAUDE.md §4, defect P5).
 */
export interface Crumb {
  readonly label: string;
  readonly to?: string;
}

export function breadcrumbsFor(currentPath: string, trailing?: string): Crumb[] {
  const crumbs: Crumb[] = [{ label: 'Dashboard', to: '/admin' }];

  const match = ADMIN_NAV.flatMap((group) => group.items).find(
    (item) => item.to !== '/admin' && isNavItemActive(item.to, currentPath),
  );

  if (match) {
    crumbs.push(trailing ? { label: match.label, to: match.to } : { label: match.label });
  }
  if (trailing) crumbs.push({ label: trailing });

  return crumbs;
}
